/**
 * Candidate AG Grid column definitions factory.
 *
 * Returns ColDef[] based on which columns are requested and the display mode.
 * Cell renderers are imported from the shared dataGridCells module.
 */

import { type ColDef } from 'ag-grid-community';
import {
  NameCellRenderer,
  PartyCellRenderer,
  DistrictCellRenderer,
  ContactCellRenderer,
  PositionCellRenderer,
  NotesCellRenderer,
  WorkspacesCellRenderer,
  CustomFieldsCellRenderer,
  FundsCellRenderer,
  StatusCellRenderer,
  IncumbentCellRenderer,
  YearCellRenderer,
  OfficeCellRenderer,
  WebSocialCellRenderer,
  StaffCellRenderer,
  ActionsCellRenderer,
} from '../shared/dataGridCells';
import { ActionsGridHeader } from '../ActionsHeaderHelp';
import { CandidateTableColumn } from './CandidateTable';

const HEADER_CLASS = 'ag-data-grid-header-uppercase';

export interface CandidateColumnOptions {
  columns: CandidateTableColumn[];
  mode?: 'directory' | 'workspace' | 'org-report';
}

export const CANDIDATE_SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  office: 'office_sought',
  district: 'district',
  party: 'party',
  status: 'status',
  incumbent: 'seat_incumbent_name',
  funds: 'total_funds_raised',
};

export function buildCandidateColumns({ columns }: CandidateColumnOptions): ColDef[] {
  const cols: ColDef[] = [];

  for (const col of columns) {
    switch (col) {
      case 'name':
        cols.push({
          colId: 'name',
          headerName: 'NAME',
          pinned: 'left',
          width: 200,
          minWidth: 160,
          sortable: true,
          suppressMovable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: NameCellRenderer,
          valueGetter: (p: any) => {
            const d = p.data;
            if (!d) return '';
            const last = d.lastName || d.last_name || '';
            const first = d.firstName || d.first_name || '';
            return (last && first) ? `${last}, ${first}` : d.fullName || d.full_name || '';
          },
        });
        break;

      case 'office':
        cols.push({
          colId: 'office',
          headerName: 'OFFICE',
          flex: 1.5,
          minWidth: 120,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: OfficeCellRenderer,
          valueGetter: (p: any) => p.data?.officeSought || '',
        });
        break;

      case 'district':
        cols.push({
          colId: 'district',
          headerName: 'DISTRICT',
          flex: 1,
          minWidth: 80,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: DistrictCellRenderer,
          valueGetter: (p: any) => p.data?.district || '',
        });
        break;

      case 'party':
        cols.push({
          colId: 'party',
          headerName: 'PARTY',
          flex: 1,
          minWidth: 90,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: PartyCellRenderer,
          valueGetter: (p: any) => p.data?.partyAffiliation || '',
        });
        break;

      case 'year':
        cols.push({
          colId: 'year',
          headerName: 'YEAR',
          flex: 1,
          minWidth: 70,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: YearCellRenderer,
          valueGetter: (p: any) => p.data?.electionYear || '',
        });
        break;

      case 'status':
        cols.push({
          colId: 'status',
          headerName: 'STATUS',
          flex: 1,
          minWidth: 90,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: StatusCellRenderer,
          valueGetter: (p: any) => p.data?.status || '',
        });
        break;

      case 'incumbent':
        cols.push({
          colId: 'incumbent',
          headerName: 'INCUMBENT',
          flex: 1.5,
          minWidth: 130,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: IncumbentCellRenderer,
          valueGetter: (p: any) => p.data?.seatIncumbentName || '',
        });
        break;

      case 'funds':
        cols.push({
          colId: 'funds',
          headerName: 'FUNDS RAISED',
          flex: 1,
          minWidth: 100,
          sortable: true,
          headerClass: HEADER_CLASS,
          cellRenderer: FundsCellRenderer,
          valueGetter: (p: any) => p.data?.totalFundsRaised || 0,
        });
        break;

      case 'contact':
        cols.push({
          colId: 'contact',
          headerName: 'CONTACT',
          flex: 2,
          minWidth: 140,
          sortable: false,
          headerClass: HEADER_CLASS,
          autoHeight: true,
          cellRenderer: ContactCellRenderer,
        });
        break;

      case 'web':
        cols.push({
          colId: 'web',
          headerName: 'WEB / SOCIAL',
          flex: 2,
          minWidth: 130,
          sortable: false,
          headerClass: HEADER_CLASS,
          autoHeight: true,
          cellRenderer: WebSocialCellRenderer,
        });
        break;

      case 'staff':
        cols.push({
          colId: 'staff',
          headerName: 'STAFF',
          flex: 1,
          minWidth: 60,
          sortable: false,
          headerClass: HEADER_CLASS,
          cellRenderer: StaffCellRenderer,
        });
        break;

      case 'position':
        cols.push({
          colId: 'position',
          headerName: 'POSITION',
          flex: 1,
          minWidth: 100,
          sortable: false,
          headerClass: HEADER_CLASS,
          cellRenderer: PositionCellRenderer,
        });
        break;

      case 'notes':
        cols.push({
          colId: 'notes',
          headerName: 'NOTES',
          flex: 1,
          minWidth: 60,
          sortable: false,
          headerClass: HEADER_CLASS,
          cellRenderer: NotesCellRenderer,
        });
        break;

      case 'custom_fields':
        cols.push({
          colId: 'custom_fields',
          headerName: 'FIELDS',
          flex: 1,
          minWidth: 60,
          sortable: false,
          headerClass: HEADER_CLASS,
          cellRenderer: CustomFieldsCellRenderer,
        });
        break;

      case 'actions':
        cols.push({
          colId: 'actions',
          headerName: 'ACTIONS',
          // 100px fit the bare label; the legend trigger needs the same bump
          // the initiatives grid took (label + 20px cell padding + the ? icon).
          width: 130,
          minWidth: 110,
          sortable: false,
          suppressMovable: true,
          headerClass: HEADER_CLASS,
          headerComponent: ActionsGridHeader,
          cellRenderer: ActionsCellRenderer,
        });
        break;

      case 'workspaces':
        cols.push({
          colId: 'workspaces',
          headerName: 'WORKSPACES',
          flex: 2,
          minWidth: 160,
          sortable: false,
          headerClass: HEADER_CLASS,
          autoHeight: true,
          cellRenderer: WorkspacesCellRenderer,
        });
        break;

      default:
        break;
    }
  }

  return cols;
}
